import { withContext } from "@/common/utils/withContext";
import { useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import Grid from "@mui/material/Grid";
import { FormControlLabel, Box } from "@mui/material";
import { EvesCheckbox } from "@/common/components/atoms/checkbox/checkbox";
import { isSuperAdmin } from "@/common/utils/utils";
import { useTranslation } from "react-i18next";

type NotificationOption = {
  name: string;
  labelKey: string;
};

const Notifications = (props: any) => {
  const userForm = props.userForm;
  const isUserSuperAdmin = isSuperAdmin();
  const { t: translate } = useTranslation();
  const savedNotifications = useRef<any>(null);

  const userNotificationOptions: NotificationOption[] = [
    { name: "notifications.sendSessionStarted", labelKey: "notifications.session_started" },
    { name: "notifications.sendOptimalChargeReached", labelKey: "notifications.optimal_charge_reached" },
    { name: "notifications.sendEndOfCharge", labelKey: "notifications.end_of_charge" },
    { name: "notifications.sendEndOfSession", labelKey: "notifications.end_of_session" },
    { name: "notifications.sendUserAccountStatusChanged", labelKey: "notifications.user_account_status_changed" },
    { name: "notifications.sendSessionNotStarted", labelKey: "notifications.session_not_started" },
    { name: "notifications.sendUserAccountInactivity", labelKey: "notifications.user_account_inactivity" },
    { name: "notifications.sendPreparingSessionNotStarted", labelKey: "notifications.preparing_session_not_started" },
    { name: "notifications.sendBillingNewInvoice", labelKey: "notifications.billing_new_invoice" },
  ];
  const adminNotificationOptions: NotificationOption[] = [
    { name: "notifications.sendExpiringRFID", labelKey: "notifications.rfid_expiring" },
    { name: "notifications.sendUnknownUserBadged", labelKey: "notifications.unknown_user_badged" },
    { name: "notifications.sendChargingStationStatusError", labelKey: "notifications.charging_station_status_error" },
    { name: "notifications.sendChargingStationRegistered", labelKey: "notifications.charging_station_registered" },
    { name: "notifications.sendOfflineChargingStations", labelKey: "notifications.charging_stations_offline" },
    { name: "notifications.sendOcpiPatchStatusError", labelKey: "notifications.ocpi_patch_status_error" },
    { name: "notifications.sendOicpPatchStatusError", labelKey: "notifications.oicp_patch_status_error" },
    { name: "notifications.sendBillingSynchronizationFailed", labelKey: "notifications.billing_synchronization_failed" },
    { name: "notifications.sendBillingPeriodicOperationFailed", labelKey: "notifications.billing_periodic_operation_failed" },
    { name: "notifications.sendComputeAndApplyChargingProfilesFailed", labelKey: "notifications.compute_and_apply_charging_profiles_failed" },
    { name: "notifications.sendEndUserErrorNotification", labelKey: "notifications.end_user_error_notification" },
    { name: "notifications.sendAdminAccountVerificationNotification", labelKey: "notifications.admin_account_verification" },
    { name: "notifications.sendOCPPStopTransactionErrorNotification", labelKey: "notifications.charging_station_error" },
  ];
  const superAdminNotificationOptions: NotificationOption[] = [
    { name: "notifications.sendCarCatalogSynchronizationFailed", labelKey: "notifications.car_catalog_synchronization_failed" },
  ];

  const clearNotifications = () => {
    const notifications = userForm.getValues().notifications || {};
    savedNotifications.current = { ...notifications };
    // EVES-4731: notification form save button visibility
    // Author: Abhishek Bharti
    // Date: 04/24/2026
    Object.keys(notifications).forEach((key: string) => {
      userForm.setValue(`notifications.${key}`, false, { shouldDirty: true });
    });
  };

  const selectAllNotifications = () => {
    const notifications = userForm.getValues().notifications || {};
    Object.keys(notifications).forEach((key: string) => {
      userForm.setValue(`notifications.${key}`, true, { shouldDirty: true });
    });
    savedNotifications.current = null;
  };

  const handleCheckboxValueChange = (field: any, checked: boolean) => {
    field.onChange(checked);
    userForm.setValue(field.name, checked, { shouldDirty: true });
    // EVES-4731: notification form save button visibility
    // Author: Abhishek Bharti
    // Date: 04/24/2026
    props.onNotificationChange?.();
  };

  const renderNotificationCheckbox = (option: NotificationOption, isDisabled: boolean) => (
    <Grid item xs={12} key={option.name}>
      <Controller
        name={option.name}
        control={userForm.control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <EvesCheckbox
                {...field}
                data-cy="user-notifications"
                checked={!!field.value}
                disabled={isDisabled}
                onChange={(event: any) => handleCheckboxValueChange(field, event.target.checked)}
              />
            }
            label={`${translate(option.labelKey)}`}
          />
        )}
      />
    </Grid>
  );

  const onNotificationsActiveChange = (checked: boolean) => {
    props.toggleNotifications(checked);
    if (!checked) {
      clearNotifications();
    } else {
      selectAllNotifications();
    }
  };

  useEffect(() => {
    const isActive = userForm.getValues().notificationsActive;
    props.toggleNotifications(isActive);
    if (!isActive) {
      clearNotifications();
    }
  }, []);

  if (isUserSuperAdmin)
    return (
      <>
        <Grid container direction="row">
          <Grid item xs={12}>
            <Controller
              name="notificationsActive"
              control={userForm.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <EvesCheckbox
                      {...field}
                      data-cy="user-notifications"
                      checked={!!field.value}
                      disabled={!props.isEnableAllEnabled}
                      onChange={(event: any) => {
                        const checked = event.target.checked;
                        field.onChange(checked);
                        userForm.setValue(field.name, checked, { shouldDirty: true });
                        onNotificationsActiveChange(checked);
                        props.onNotificationChange?.();
                      }}
                    />
                  }
                  label={`${translate("users.notifications_active")}`}
                />
              )}
            />
          </Grid>
        </Grid>
        <Box pl={3}>
          <Grid container direction="row">
            {superAdminNotificationOptions.map((option) => renderNotificationCheckbox(option, !props.isNotificationEnabled))}
          </Grid>
        </Box>
      </>
    );

  return (
    <>
      {props.showNotification && (
        <>
          <Grid container direction="row">
            <Grid item xs={12}>
              <Controller
                name="notificationsActive"
                control={userForm.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <EvesCheckbox
                        {...field}
                        data-cy="user-notifications"
                        checked={!!field.value}
                        disabled={!props.isEnableAllEnabled}
                        onChange={(event: any) => {
                          const checked = event.target.checked;
                          field.onChange(checked);
                          userForm.setValue(field.name, checked, { shouldDirty: true });
                          onNotificationsActiveChange(checked);
                          props.onNotificationChange?.();
                        }}
                      />
                    }
                    label={`${translate("users.notifications_active")}`}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Box pl={3}>
            <Grid container direction="row">
              {userNotificationOptions.map((option) => renderNotificationCheckbox(option, !props.isNotificationEnabled))}
            </Grid>
          </Box>
        </>
      )}
      {props.showAdminNotification && (
        <Box pl={3}>
          <Box sx={{ fontSize: "14px", padding: "6px 0" }}>{`${translate("notifications.admin_only")}`}</Box>
          <Box pl={3}>
            <Grid container direction="row">
              {adminNotificationOptions.map((option) => renderNotificationCheckbox(option, !props.isAdminNotificationEnabled))}
            </Grid>
          </Box>
        </Box>
      )}
    </>
  );
};

export default withContext(Notifications);
